import { Button, Container, Drawer, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, ArrowBack, Edit, MoreVert, Share } from "@material-ui/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Popup from "reactjs-popup";

import { triggerDebuggerAndThrow } from "../../utils/debugging-utils";
import { LoadingModal } from "../Modals";
import WorkoutPicker from "../Modals/WorkoutPicker";
import FloatingActionButton from "../Utilities/FloatingActionButton";
import WorkoutsAddComponent from "./Add";
import WorkoutsDetailsComponent from "./Details";
import WorkoutsEditComponent from "./Edit";
import WorkoutsShareComponent from "./Share";
import WorkoutsListings from "./WorkoutsListings";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  listings: {
    marginBottom: theme.spacing(5),
  },
  drawer: {
    "& .MuiDrawer-paper": {
      width: 512,
      "& #FaB": {
        bottom: 25,
      },
      "& .drawer-heading": {
        height: 75,
        backgroundColor: "var(--text-primary)",
        color: "#ffffff",
        fontSize: 20,
        fontWeight: 500,
        padding: "25px 15px",
        "& svg": {
          cursor: "pointer",
          marginRight: 15,
          marginTop: -2,
        },
        "& .session-options": {
          float: "right",
          fontSize: 14,
          fontWeight: 400,
          "& li": {
            fontSize: 14,
            fontWeight: "bold",
            padding: "5px 0",
            color: "var(--text-primary)",
            "& svg": {
              height: 24,
              width: 24,
              marginRight: 7,
            },
          },
        },
      },
      "& .drawer-body": {
        overflow: "auto",
        height: "85vh",
      },
    },
  },
  actions: {
    [theme.breakpoints.only("xs")]: {
      display: "none",
    },
    [theme.breakpoints.up("md")]: {
      position: "sticky",
    },
    "& h5": {
      [theme.breakpoints.up("sm")]: {
        marginTop: theme.spacing(3),
        textAlign: "center",
      },
      marginBottom: theme.spacing(3),
      fontSize: 14,
      fontWeight: 600,
      color: "#333333",
    },
    "& button": {
      width: "100%",
      height: 56,
      marginBottom: 12,
      padding: 19,
      backgroundColor: "var(--text-primary)",
      color: "#ffffff",
      textTransform: "none",
      textAlign: "left",
      fontSize: 16,
      fontWeight: 500,
      justifyContent: "space-between",
      "&:hover": {
        backgroundColor: "var(--text-primary)",
      },
      "& svg": {
        float: "right",
      },
    },
  },
}));

type WorkoutsActionProps = {
  setShowAdd: React.Dispatch<React.SetStateAction<boolean>>;
  showSelectWorkout: React.Dispatch<React.SetStateAction<boolean>>;
};

const WorkoutsActions: React.FC<WorkoutsActionProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Container className={classes.actions}>
      <Button onClick={() => props.showSelectWorkout(true)}>
        {t("Based on another workout")}
        <Edit />
      </Button>
      <Button onClick={() => props.setShowAdd(true)}>
        {t("Start from scratch")}
        <Add />
      </Button>
    </Container>
  );
};

// Workouts Listings w/ Floating Action Button
function WorkoutsListingScreen(_unusedProps: any) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = React.useState(false);
  const [showAdd, setShowAdd] = React.useState(false);
  const [selectWorkout, showSelectWorkout] = React.useState(false);
  const [workoutToShow, setWorkoutToShow] = React.useState<string | null>(null);
  const [showAddWorkout, setShowAddWorkout] = React.useState<string | undefined>();
  const [editWorkout, setEditWorkout] = React.useState<string | null>(null);
  const [shareWorkout, setShareWorkout] = React.useState<string | null>(null);
  const [actionMenu, setActionMenu] = React.useState(false);

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12} md={8}>
        <WorkoutsListings
          hasSession={false}
          handleSubmit={(_templateProps) => triggerDebuggerAndThrow()}
          setShowAddWorkout={setShowAddWorkout}
          setShowWorkout={setWorkoutToShow}
          /*TODO: what was this doing and how className={classes.listings}*/
          /* TODO: what was this doing hasSelector={true} */
        >
          <FloatingActionButton
            show={actionMenu}
            options={["Start from Scratch", "Based on Workout"]}
            onAdd={() => history.push("/workouts/add")}
            onClick={() => setActionMenu(!actionMenu)}
            onAddSuperset={triggerDebuggerAndThrow}
            onAddExercise={triggerDebuggerAndThrow}
            onInvite={triggerDebuggerAndThrow}
          />
        </WorkoutsListings>
      </Grid>
      <Grid item xs={12} md={4}>
        <WorkoutsActions showSelectWorkout={showSelectWorkout} setShowAdd={setShowAdd} />
      </Grid>
      {/* Drawers */}
      <WorkoutPicker
        show={selectWorkout}
        onHide={() => showSelectWorkout(false)}
        hasSelector={true}
        setShowAddWorkout={setShowAddWorkout}
      />
      <Drawer
        anchor="right"
        open={showAdd}
        onClose={() => setShowAdd(false)}
        className={classes.drawer}
      >
        <div className="drawer-heading">
          <ArrowBack onClick={() => setShowAdd(false)} />
          {t("Add workout")}
        </div>
        <div className="drawer-body">
          <WorkoutsAddComponent />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={!!showAddWorkout}
        onClose={() => setShowAddWorkout(undefined)}
        className={classes.drawer}
      >
        <div className="drawer-heading">
          <ArrowBack onClick={() => setShowAddWorkout(undefined)} />
          {t("Add workout")}
        </div>
        <div className="drawer-body">
          <WorkoutsAddComponent id={showAddWorkout} />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={!!workoutToShow}
        onClose={() => setWorkoutToShow(null)}
        className={classes.drawer}
      >
        <div className="drawer-heading">
          <ArrowBack onClick={() => setWorkoutToShow(null)} />
          {t("Workout Details")}
          <span className="session-options">
            <Popup
              position="bottom right"
              repositionOnResize
              contentStyle={{
                textAlign: "left",
                width: "215px",
                padding: 10,
                cursor: "pointer",
                borderRadius: 5,
                backgroundColor: "var(--white)",
                color: "var(--text-primary)",
              }}
              trigger={<MoreVert />}
            >
              {(close) => (
                <ul style={{ marginBottom: 0 }}>
                  <li
                    onClick={() => {
                      close();
                      return setEditWorkout(workoutToShow);
                    }}
                  >
                    <Edit />
                    {t("Edit workout")}
                  </li>
                  <li
                    onClick={() => {
                      close();
                      return setShareWorkout(workoutToShow);
                    }}
                  >
                    <Share />
                    {t("Share workout")}
                  </li>
                </ul>
              )}
            </Popup>
          </span>
        </div>
        <div className="drawer-body">
          <WorkoutsDetailsComponent
            history={history}
            location={{ ...window.location, state: undefined }}
            match={{
              params: { id: workoutToShow ? workoutToShow : "" },
              isExact: true,
              url: window.location.href,
              path: window.location.pathname,
            }}
          />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={!!editWorkout}
        onClose={() => setEditWorkout(null)}
        className={classes.drawer}
      >
        <div className="drawer-heading">
          <ArrowBack onClick={() => setEditWorkout(null)} />
          {t("Edit workout")}
        </div>
        <div className="drawer-body">
          <WorkoutsEditComponent match={{ params: { id: editWorkout } }} />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={!!shareWorkout}
        onClose={() => setShareWorkout(null)}
        className={classes.drawer}
      >
        <div className="drawer-heading">
          <ArrowBack onClick={() => setShareWorkout(null)} />
          {t("Share workout")}
        </div>
        {shareWorkout && (
          <div className="drawer-body">
            <WorkoutsShareComponent
              loading={loading}
              id={shareWorkout}
              setLoading={setLoading}
              setShowWorkout={setWorkoutToShow}
              showWorkout={workoutToShow}
              setShareWorkout={setShareWorkout}
            />
          </div>
        )}
      </Drawer>
      {/* Modals */}
      <LoadingModal show={loading} onHide={() => setLoading(false)} />
    </Grid>
  );
}

export default WorkoutsListingScreen;
